import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Helmet } from 'react-helmet';
import { Key } from 'lucide-react';

export default function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(location?.state?.message || '');

  useEffect(() => {
    // Clear success message after 5 seconds
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    
    // Client-side validation
    if (!email?.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      const data = await signIn(email?.trim(), password);
      
      // Verify sign in was successful
      if (data?.user) {
        // CRITICAL FIX: Let the routing system handle the redirect based on user role
        // Don't try to navigate here - the protected routes will handle it
        // Check user role and navigate to appropriate starting point
        const userRole = data?.profile?.role;
        
        if (userRole === 'admin') {
          // Navigate to admin dashboard and let AdminRoute handle protection
          navigate('/admin-dashboard', { replace: true });
        } else {
          // Navigate to company selection for employees
          navigate('/company-selection-hub', { replace: true });
        }
      } else {
        setError('Sign in failed. Please try again.');
      }
    } catch (err) {
      // Display the error message from AuthContext
      setError(err?.message || 'An error occurred during sign in. Please try again.');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setResetSuccess(false);

    if (!resetEmail?.trim()) {
      setError('Please enter your email address');
      return;
    }

    setResetLoading(true);

    try {
      await resetPassword(resetEmail?.trim());
      setResetSuccess(true);
      setResetEmail('');
    } catch (err) {
      setError(err?.message || 'Failed to send password reset email. Please try again.');
      console.error('Password reset error:', err);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In - CorperConnect</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium text-center">
                {successMessage}
              </p>
            </div>
          )}

          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Sign in to CorperConnect
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div className="rounded-md shadow-sm -space-y-px">
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                className="mb-4"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot your password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
                  Sign up
                </Link>
              </p>
            </div>
          </form>

          {/* Demo Credentials Section */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
              <Key className="w-4 h-4 mr-2" />
              Demo Test Credentials
            </h3>
            <div className="space-y-2 text-sm">
              <div className="bg-white rounded p-2">
                <div className="font-medium text-gray-700">Admin Account:</div>
                <div className="text-gray-600">Email: admin@corperconnect.com</div>
                <div className="text-gray-600">Password: AdminPass123!</div>
              </div>
            </div>
            <p className="mt-3 text-xs text-blue-700">
              Use these credentials to test the admin user management system
            </p>
          </div>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Reset Password
                  </h3>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetSuccess(false);
                      setError('');
                      setResetEmail('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {resetSuccess ? (
                  <div className="rounded-md bg-green-50 p-4 mb-4">
                    <p className="text-sm text-green-800">
                      Password reset email sent! Please check your inbox and follow the instructions.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    {error && (
                      <div className="rounded-md bg-red-50 p-4">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    )}
                    <p className="text-sm text-gray-600 mb-4">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e?.target?.value)}
                      required
                    />
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setResetSuccess(false);
                          setError('');
                          setResetEmail('');
                        }}
                        className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={resetLoading}
                        className="flex-1"
                      >
                        {resetLoading ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}